import React, { PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { RadioGroup, RadioGroupItem } from '@swyftx/aviary/atoms/Radio';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { RangeDatePicker } from '@swyftx/aviary/molecules/RangeDatePicker';

import { MarginTransactionsReportDownloadButton } from './MarginTransactionsReportDownloadButton';
import { useMarginTransactionsReport } from './useMarginTransactionsReport';

const MarginTransactionsReportModal: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const {
    reportingPeriods,
    reportPeriod,
    setReportPeriod,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    reset,
    isValid,
    startDate,
    endDate,
    timezone,
  } = useMarginTransactionsReport();
  const { t } = useTranslation('margin');

  const onClose = useCallback(
    (o?: boolean) => {
      if (!o) reset();
    },
    [reset],
  );

  return (
    <Modal
      triggerElement={children}
      title={t('transactionReports.title')}
      position='dynamic'
      className='lg:min-w-[400px]'
      onOpenChange={onClose}
    >
      <FlexLayout direction='column' className='px-24 pb-24' spacing={24}>
        <Body color='secondary'>{t('transactionReports.description')}</Body>

        <FlexLayout spacing={12} direction='column'>
          <Body weight='emphasis' size='small'>
            {t('transactionReports.selectPeriod')}
          </Body>
          <RadioGroup value={reportPeriod} onValueChange={setReportPeriod}>
            <FlexLayout direction='column' spacing={12}>
              {reportingPeriods.map((period) => (
                <FlexLayout
                  key={period.value}
                  alignItems='center'
                  spacing={8}
                  onClick={() => setReportPeriod(period.value)}
                  className='cursor-pointer'
                >
                  <RadioGroupItem value={period.value} className='text-color-text-info' />
                  <Body color='primary'>{period.title}</Body>
                </FlexLayout>
              ))}
            </FlexLayout>
          </RadioGroup>

          {reportPeriod === 'custom' && (
            <RangeDatePicker
              className='mt-32'
              dateFrom={dateFrom}
              dateTo={dateTo}
              setDateFrom={setDateFrom}
              setDateTo={setDateTo}
            />
          )}
        </FlexLayout>

        <MarginTransactionsReportDownloadButton
          isValid={isValid}
          startDate={startDate}
          endDate={endDate}
          timezone={timezone}
        />
      </FlexLayout>
    </Modal>
  );
};

export { MarginTransactionsReportModal };
